import React, { createRef } from 'react';
import { fabric } from 'sunzi-fabric';
import { getTrimImage, loadImageFromURL } from '@/utils';
import { Theme } from '@/sunzi';
import { RotateVernier, Wrapper, Loading } from '..';
import styles from './style.less';

const padding = 40;

interface CropaiProps {
  /** 头部渲染 */
  renderHeader: React.ReactNode;
  /** 用户原图 */
  source: string;
  /** crop图 */
  cropImage?: string;
  /** ai图 */
  aiImage?: string;
  /** gtag对象 */
  gtag: any;
}

interface CropaiState {
  loading: boolean;
}

class Cropai extends React.PureComponent<CropaiProps, CropaiState> {
  private _content: React.RefObject<HTMLDivElement> = createRef();
  private _element: React.RefObject<HTMLCanvasElement> = createRef();
  private _canvas?: fabric.Canvas;
  private _source?: fabric.Image;
  private _aiImage?: fabric.Image;

  static defaultProps = {
    maxWidth: 1200,
    maxHeight: 1200,
  }

  constructor(props: CropaiProps) {
    super(props);
    this.state = {
      loading: true
    }
  }

  async componentDidMount() {
    const { source, cropImage, aiImage } = this.props;
    if (this._content.current && this._element.current && cropImage) {
      let { offsetWidth, offsetHeight } = this._content.current;
      offsetWidth -= padding, offsetHeight -= padding;
      // 切割图片
      const { image, imageAsync } = await getTrimImage(cropImage, aiImage);
      const _cropImage = await loadImageFromURL(image);
      const { width: cropWidth = 0, height: cropHeight = 0 } = _cropImage;
      this._canvas = new fabric.Canvas(this._element.current, {
        selection: false,
      });
      let width = offsetWidth;
      let height = offsetWidth * cropHeight / cropWidth;
      if (cropWidth < cropHeight) {
        height = offsetHeight;
        width = offsetHeight * cropWidth / cropHeight;
      }
      const scale = width / cropWidth;
      _cropImage.scale(scale);
      this._canvas.setDimensions({ width, height });
      this.setOverlayImage(_cropImage);
      // 放置用户图片
      this._source = await loadImageFromURL(source);
      const { width: sourceWidth = 0, height: sourceHeight = 0 } = this._source;
      this._source.set({
        left: width / 2,
        top: height / 2,
        originX: 'center',
        originY: 'center',
        hasBorders: false,
        hasControls: false,
      }).scale(Math.max(width / sourceWidth, height / sourceHeight));
      this._canvas.add(this._source);
      this._canvas.renderAll();
      this.setState({
        loading: false
      });
      // 处理ai图
      if (imageAsync)
        this._aiImage = await loadImageFromURL(imageAsync, {
          scaleX: scale,
          scaleY: scale
        });
    }
  }

  setOverlayImage = (image: fabric.Image) => new Promise(resolve => {
    this._canvas?.setOverlayImage(image, resolve);
  });

  public async onConfirm() {
    let ai;
    const crop = this._canvas?.toDataURL({ format: 'jpeg', quality: 0.8 });
    if (this._aiImage) {
      this.setOverlayImage(this._aiImage);
      this._canvas?.renderAll();
      const result = this._canvas?.getElement().toDataURL();
      if (result)
        ai = (await getTrimImage(result)).image;
    }
    return { crop, ai };
  }

  // 缩放
  handleZoom = (value: number) => () => {
    if (this._source) {
      const scale = this._source.scaleX || 1;
      this._source.scale(scale * value);
      this._canvas?.renderAll();
    }
  }

  // 逆时针旋转
  handleRotate = (value: number) => () => {
    if (this._source) {
      const { angle = 0 } = this._source;
      this._source.rotate(angle + value);
      this._canvas?.renderAll();
    }
  }

  // 旋转控件滚动条改变
  handleVernierChange = (rotate: number) => {
    this._source?.rotate(rotate);
    this._canvas?.renderAll();
  }

  render() {
    const { loading } = this.state;
    const { renderHeader } = this.props;

    return (
      <div className={styles.cropai}>
        {renderHeader}
        <div ref={this._content} className={styles.cropaiContent}>
          <canvas ref={this._element} />
          {loading && 
            <Wrapper className={styles.cropaiContentLoading}>
              <Loading />
            </Wrapper>
          }
        </div>
        <div className={styles.cropaiFooter}>
          <RotateVernier
            theme={Theme.light}
            onZoomIn={this.handleZoom(1.1)}
            onZoomOut={this.handleZoom(0.9)}
            onRotateLeft={this.handleRotate(-5)}
            onRotateRight={this.handleRotate(5)}
            onVernierChange={this.handleVernierChange}
          />
        </div>
      </div>
    );
  }
}

export default Cropai;